/**
 * A dial with a target mark.
 *
 * The target is the point of it. A number on its own ("94%") means nothing
 * without the bar it is being held to, and a gauge that only shows the needle
 * is a stat tile that takes more room. The tick on the arc is where you agreed
 * to be; the fill is where you are.
 *
 * Colour follows distance from target, not an absolute band, because the same
 * 78% is excellent for billable utilisation and dire for SLA attainment.
 * `lowerIsBetter` turns it round for a measure where less is the goal — days
 * to finish a project — so under the target is the good side of the tick.
 * Navy means at or above target — this brand has no green, and "good" is the
 * structural colour. Crimson is reserved for genuinely short of the mark.
 */
import { Link } from 'react-router-dom';

/* Three-quarter dial: opens at the bottom so the needle sweeps left to right
   the way a speedometer does. */
const START = -Math.PI * 1.25;
const SWEEP = Math.PI * 1.5;

function pointOn(cx: number, cy: number, r: number, angle: number): [number, number] {
  return [cx + r * Math.cos(angle), cy + r * Math.sin(angle)];
}

function arc(cx: number, cy: number, r: number, from: number, to: number): string {
  const [x0, y0] = pointOn(cx, cy, r, from);
  const [x1, y1] = pointOn(cx, cy, r, to);
  const large = Math.abs(to - from) > Math.PI ? 1 : 0;
  return `M${x0},${y0} A${r},${r} 0 ${large} 1 ${x1},${y1}`;
}

export function Gauge({
  label,
  value,
  target,
  max = 100,
  unit = '%',
  decimals = 0,
  hint,
  to,
  onClick,
  drillLabel = 'See the detail',
  lowerIsBetter = false,
}: {
  label: string;
  /** Null renders an honest empty dial rather than a zero. */
  value: number | null;
  target: number | null;
  max?: number;
  unit?: string;
  decimals?: number;
  hint?: string;
  /** Makes the whole dial a link to the evidence behind the number. Only set
   *  where such a page exists — a gauge that looks clickable and is not is
   *  worse than one that plainly is not. */
  to?: string;
  /** The same, for a drill-in that opens on this page (a drawer) rather than
   *  going to another one. */
  onClick?: () => void;
  drillLabel?: string;
  /** For a measure where less is better, such as days to complete. */
  lowerIsBetter?: boolean;
}) {
  const size = 168;
  const cx = size / 2;
  const cy = size / 2;
  const r = 62;

  const clamp = (n: number) => Math.max(0, Math.min(1, n / max));
  const fraction = value === null ? 0 : clamp(value);
  const targetFraction = target === null ? null : clamp(target);

  const valueAngle = START + SWEEP * fraction;
  const targetAngle = targetFraction === null ? null : START + SWEEP * targetFraction;

  /* Three states, not two. A gauge that goes straight from met to crimson
     makes 94% against a 95% target look like a fire, and people stop reading
     a dial that is always red. "Near" is within 10% of the bar — visibly
     short, not alarming. */
  const state =
    value === null || target === null
      ? 'none'
      : lowerIsBetter
        ? value <= target
          ? 'good'
          : value <= target * 1.1
            ? 'near'
            : 'poor'
        : value >= target
          ? 'good'
          : value >= target * 0.9
            ? 'near'
            : 'poor';

  const body = (
    <>
      <svg viewBox={`0 0 ${size} ${size * 0.82}`} role="img" aria-label={`${label}: ${value === null ? 'not measured' : `${value}${unit}`}${target === null ? '' : `, target ${target}${unit}`}`}>
        <path className="gauge-track" d={arc(cx, cy, r, START, START + SWEEP)} fill="none" />
        {value !== null && fraction > 0 && (
          <path className={`gauge-fill ${state}`} d={arc(cx, cy, r, START, valueAngle)} fill="none" />
        )}

        {/* The bar you agreed to clear. */}
        {targetAngle !== null && (
          <line
            className="gauge-target"
            x1={pointOn(cx, cy, r - 11, targetAngle)[0]}
            y1={pointOn(cx, cy, r - 11, targetAngle)[1]}
            x2={pointOn(cx, cy, r + 11, targetAngle)[0]}
            y2={pointOn(cx, cy, r + 11, targetAngle)[1]}
          />
        )}

        <text className="gauge-value" x={cx} y={cy + 2} textAnchor="middle">
          {value === null ? '—' : `${value.toFixed(decimals)}${unit}`}
        </text>
        <text className="gauge-target-label" x={cx} y={cy + 24} textAnchor="middle">
          {target === null ? 'no target' : `target ${target.toFixed(decimals)}${unit}`}
        </text>
      </svg>
      <div className="gauge-label">{label}</div>
      {hint && <div className="gauge-hint xs muted">{hint}</div>}
      {(to || onClick) && <div className="gauge-drill xs">{drillLabel} →</div>}
    </>
  );

  if (onClick) {
    return (
      <button type="button" className="gauge gauge-link gauge-button" onClick={onClick}>
        {body}
      </button>
    );
  }
  return to ? (
    <Link className="gauge gauge-link" to={to}>
      {body}
    </Link>
  ) : (
    <div className="gauge">{body}</div>
  );
}

/** Direction of travel against the previous period. */
export function trendArrow(current: number | null, previous: number | null): string {
  if (current === null || previous === null) return '';
  const delta = current - previous;
  if (Math.abs(delta) < 0.05) return '→';
  return delta > 0 ? '↑' : '↓';
}
